<template>
  <div v-if="data">
    <v-alert v-if="createError" type="error" outlined>{{ createError }}</v-alert>
    <v-card v-else shaped outlined>
      <v-card-title>Данные о ТА ЛП (РУ, ГО, ГЗ)</v-card-title>
      <v-card-text>
        <HelpCard open outlined class="mb-4">
          <div class="text-body-2">
            <p class="font-weight-bold mb-2">
              1. Оформление информации о количестве действующих РУ и сроках их
              действия для предлагаемого ЛП и ТА ЛП
            </p>
            <ul class="mb-3">
              <li>Если РУ одно — указываем только его;</li>
              <li>
                Если после перерегистрации на 1 ЛП есть 2 РУ (старое
                российское и новое по ЕАЭС) — учитываем только одно РУ, на
                которое перерегистрировался ЛП — по ЕАЭС;
              </li>
              <li>
                Если РУ &gt;1 — указываем общее количество уникальных РУ
                цифрой;
              </li>
              <li>
                Срок действия РУ смотрим по максимально возможному, например
                есть 3 РУ, РУ №1 — срок до 25.12.2026, РУ №2 — срок до
                25.06.2026, РУ №3 — срок Бессрочный, в строку вписываем
                Бессрочный.
              </li>
            </ul>

            <p class="font-weight-bold mb-2">
              2. Оформление информации об обращении ЛП
            </p>
            <p class="mb-1">2.1 Для предлагаемого ЛП:</p>
            <ul class="mb-2">
              <li>
                в 1 квартал приводим данные о ГО только за календарный год,
                предшествующий подаче;
              </li>
              <li>
                во 2–4 кварталах приводим данные о ГО за текущий год и
                предыдущий календарный год.
              </li>
            </ul>
            <p class="mb-1">2.2 Для ТА ЛП приводим данные только за предыдущий календарный год.</p>
            <p class="mb-1">
              2.3 Для всех ЛП (предлагаемый и ТА ЛП) при превышении закупок
              над обращениями (в ГО вышло мало упаковок*) дополнительно
              приводим данные за позапрошлый год.
            </p>
            <p class="mb-1 text-caption">
              *Ситуации, когда разница между ГО и ГЗ требует уточнения и
              предоставления данных за позапрошлый год:
            </p>
            <ul class="mb-2 text-caption">
              <li>
                разница &gt; 4 тыс. упаковок для препарата, выпущенного
                объёмом 20–50 тыс. упаковок;
              </li>
              <li>
                разница &gt; 10 тысяч упаковок для препарата, выпущенного
                объёмом 50–100 тыс. упаковок;
              </li>
              <li>
                разница &gt; 30 тысяч для препарата, выпущенного объёмом
                несколько сотен тысяч;
              </li>
              <li>
                разница &gt; 300 тыс. упаковок для препарата, выпущенного
                объёмом несколько миллионов.
              </li>
            </ul>
            <p class="mb-1 text-caption">Пример оформления:</p>
            <pre class="text-caption mb-3">2026 г. - 0
2025 г. - 13 000</pre>

            <p class="font-weight-bold mb-2">3. Гос. закупки</p>
            <p class="mb-1">1) Для предлагаемого ЛП:</p>
            <ul class="mb-2">
              <li>
                в 1 квартал приводим данные о закупках только за календарный
                год, предшествующий подаче;
              </li>
              <li>
                во 2–4 кварталах приводим данные о закупках за текущий год и
                предыдущий календарный год.
              </li>
            </ul>
            <p class="mb-1 text-caption">Пример оформления:</p>
            <pre class="text-caption mb-2">2026 г. - 10 000 (3 000)
2025 г. - 5 000 (2 000)</pre>
            <p class="mb-0">
              2) Для ТА ЛП приводим данные только за предыдущий календарный
              год.
            </p>
          </div>
        </HelpCard>

        <v-row dense class="mb-3">
          <v-col cols="12" md="6" lg="4">
            <SelectDate
              v-if="editMode"
              v-model="data.taLp.analysisDate"
              :maxDate="currentDate"
              label="Дата анализа"
              @input="doChange(true)"
            />
            <div v-else class="body-2">
              <span class="font-weight-medium">Дата анализа: </span>
              <span>{{ formattedAnalysisDate }}</span>
            </div>
          </v-col>
        </v-row>

        <UpdateTaLp
          :log-view="logView"
          :local-file="data.taLp.file"
          :edit-mode="editMode"
          @change="onFileChange"
          @parse-error="onParseError"
        />

        <v-alert
          v-if="parseError"
          type="error"
          outlined
          dense
          class="mt-2"
        >
          {{ parseError }}
        </v-alert>

        <div v-if="data.taLp.sheetName" class="text-caption text--secondary mt-3 mb-1">
          Лист: {{ data.taLp.sheetName }}
        </div>

        <TaLpTableGrid
          v-if="data.taLp.rows && data.taLp.rows.length"
          class="mt-2"
          :rows="data.taLp.rows"
          :note="data.taLp.note"
        />

        <v-alert
          v-else-if="!data.taLp.file.filename"
          type="info"
          outlined
          dense
          class="mt-3"
        >
          Загрузите файл Excel с листом, имя которого начинается с «МНН».
        </v-alert>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import AnalogMixin from '@/components/expAnalogs/AnalogMixin'
import HelpCard from '@/components/HelpCard'
import SelectDate from '@/components/editors/SelectDate'
import UpdateTaLp from '@/components/expAnalogs/taLp/UpdateTaLp'
import TaLpTableGrid from '@/components/expAnalogs/taLp/TaLpTableGrid'
import { DEFAULT_TA_LP } from '@/components/expAnalogs/const'

export default {
  name: 'TaLpData',
  components: { HelpCard, SelectDate, UpdateTaLp, TaLpTableGrid },
  mixins: [AnalogMixin],
  props: {
    value: {
      type: Object,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    onCreate: {
      type: Function,
      default: null,
    },
  },
  data: () => ({
    parseError: '',
  }),
  computed: {
    currentDate() {
      return new Date().toISOString().slice(0, 10)
    },
    formattedAnalysisDate() {
      const d = this.data?.taLp?.analysisDate
      if (!d) return '—'
      const [y, m, day] = String(d).split('-')
      if (y && m && day) return `${day}.${m}.${y}`
      return d
    },
  },
  created() {
    this.ensureTaLp()
  },
  methods: {
    ensureTaLp() {
      if (this.data && !this.data.taLp) {
        this.$set(this.data, 'taLp', DEFAULT_TA_LP())
      }
    },
    onFileChange(payload, force = false) {
      this.ensureTaLp()
      if (payload.localFile) {
        this.data.taLp.file = payload.localFile
      }
      this.data.taLp.sheetName = payload.sheetName || ''
      this.data.taLp.rows = payload.rows || []
      this.data.taLp.note = payload.note || ''
      this.parseError = ''
      this.doChange(force)
    },
    onParseError(message) {
      this.parseError = message
    },
  },
}
</script>

<style scoped>
pre {
  white-space: pre-wrap;
  font-family: inherit;
  margin: 0;
}
</style>
